import { $, $$, YO, alcance, aviso, el, esc, limpiar, noContesto, pedir, rpc, sb, tengoRol } from './nucleo';

let PERMISOS_CAT: { recurso: string; accion: string }[] = [];
let ROLES: { codigo: string; nombre: string }[] = [];
let USUARIOS: any[] = [];
//  EN QUÉ ESTADO ESTÁN LOS CATÁLOGOS: TRES estados, no dos. `E_ROLES`/
//  `E_PERMISOS` dicen si la lectura FALLÓ, pero mientras está en viaje todo
//  está vacío, igual que si la base hubiera contestado "no hay ninguno".
//
//  Es caro: con los arreglos vacíos el editor manda `p_roles: []` y GUARDAR le
//  saca a la persona todos los roles y permisos sueltos. `cargarUsuarios()` no
//  espera la lectura (con red mala, siete segundos, D92) y pinta la tabla con
//  sus botones. Por eso la bandera es TERCERA y no un `!ROLES.length`: "vino
//  vacío" es distinto de "todavía no vino".
let E_ROLES = '', E_PERMISOS = '', CATALOGOS_LEIDOS = false;

//  La TERCERA LECTURA (0049): con qué alcance da cada ROL cada permiso
//  (`v_rol_permisos`). `app.alcance()` toma el MÁXIMO entre rol y permiso
//  suelto, así que un suelto más chico que el del rol NO RECORTA NADA; sin
//  esto el selector ofrecería un control que a veces no hace nada.
//
//  Bandera y cartel propios, porque puede fallar sola. Ojo: NO TRABA GUARDAR
//  (el alcance que se guarda es el elegido): es información, no un freno.
//
//  Va APARTE del `Promise.all` de las otras dos: si entrara, `CATALOGOS_LEIDOS`
//  esperaría a las TRES y el alta (que sólo necesita `roles`) quedaría apagada.
//
//  `VIAJE` es el número de intento: una respuesta vieja que llega después de
//  la nueva pisaría lo bueno. Sólo escribe el estado el viaje en curso.
let ROL_PERMS: { rol: string; permiso: string; alcance: string }[] = [];
let E_ROLPERM = '', ROLPERM_LEIDO = false, VIAJE = 0;

//  Los tres valores del CHECK `usuario_permisos_alcance_check`, de menor a
//  mayor. Escritos acá y no deducidos de las filas: el `<select>` quedaría sin
//  opciones el día que ninguna fila use `equipo`.
//  El PESO es lo que hace `app.alcance()` (0066): `equipo` aún vale lo mismo
//  que `propio`, así que un `equipo` suelto sobre un rol `propio` «no cambia
//  nada» y el chip lo dice.
const ALCANCES = ['propio', 'equipo', 'todos'] as const;
const PESO: Record<string, number> = { propio: 1, equipo: 1, todos: 3 };

//  LAS PREGUNTAS DE ESTA PANTALLA SON DISTINTAS (D102) y pueden estar en
//  desacuerdo sobre la misma fila:
//
//  · ABRIR la pestaña: `puede('usuario','gestionar')`, sin mirar el alcance
//    (`arranque.ts`). Con alcance `propio` se entra igual.
//  · LEER el padrón: `v_usuarios` sale de `app.usuarios_visibles()`, cuyo
//    `where` es `alcance('usuario','gestionar') = 'todos' OR
//    alcance('usuario','ver_datos_personales') = 'todos'`. No recorta: o llega
//    entero o no llega NADA. El segundo permiso solo no abre la pestaña.
//  · LEER el rastro: `usuario_eventos` pide `alcance('usuario','gestionar') =
//    'todos' OR usuario_id = yo`. Ése sí RECORTA (D97) y
//    `ver_datos_personales` no lo abre.
//  · ACTUAR: las cuatro funciones empiezan por `app.exigir_gestion_usuarios()`,
//    que exige `alcance('usuario','gestionar') = 'todos'` y tira 42501.
//
//  Estado alcanzable: `usuario.gestionar:propio` + `ver_datos_personales:todos`
//  ve el padrón entero y no puede tocar una fila.
const veElPadron = () =>
  alcance('usuario', 'gestionar') === 'todos' ||
  alcance('usuario', 'ver_datos_personales') === 'todos';
const gestiona = () => alcance('usuario', 'gestionar') === 'todos';

//  El texto tiene que saber CUÁL de las dos causas es la suya (D87): alcance
//  recortado no es lo mismo que no tener el otro permiso.
const porQueNoVeElPadron = () => {
  const g = alcance('usuario', 'gestionar');
  const v = alcance('usuario', 'ver_datos_personales');
  const falta = v === null
    ? 'y no tenés «ver datos personales de todos»'
    : `y el tuyo de «ver datos personales» es «${esc(v)}»`;
  return `Tenés «gestionar usuarios» con alcance «${esc(g ?? '—')}» ${falta}. ` +
         `El padrón lo devuelve entero o no devuelve nada: con estos alcances ` +
         `la base no manda ninguna fila. <b>No quiere decir que no haya nadie cargado.</b>`;
};

//  LOS PERMISOS SUELTOS DE UNA FILA (0049): `permisos_sueltos` es
//  `{"recurso.accion": "alcance"}` y `permisos_extra` son sus CLAVES; en la
//  pantalla se usa sólo el objeto. El `typeof` es por si PostgREST devuelve
//  otra cosa: mejor no mostrar ninguno que reventar la fila.
const sueltosDe = (u: any): Record<string, string> => {
  const m = u?.permisos_sueltos;
  return (m && typeof m === 'object' && !Array.isArray(m)) ? m : {};
};

// ---------- los catálogos del alta y del editor ----------
//  Cada lectura del `Promise.all` necesita su bandera y su cartel: una puede
//  fallar y la otra no. Ojo: no tragarse el `error` con `|| []`; con `roles`
//  caído el alta crearía a la persona SIN NINGÚN ROL.
export async function cargarRoles(){
  //  Vuelve a "todavía no llegaron" en cada intento (el reintento llama acá).
  CATALOGOS_LEIDOS = false;
  ROLPERM_LEIDO = false;
  E_ROLPERM = '';
  ROL_PERMS = [];
  const viaje = ++VIAJE;
  pintarPuerta();

  //  La tercera se larga acá y se espera aparte. Sólo se pide si se puede
  //  actuar: sólo la usa el EDITOR, que no se abre sin `gestionar:todos`
  //  (`accionesDe` no dibuja el botón).
  (gestiona()
    ? pedir(sb.from('v_rol_permisos').select('rol,permiso,alcance'))
    : Promise.resolve({ data: [], error: null } as any)
  ).then((r: any) => {
    if (viaje !== VIAJE) return;
    E_ROLPERM = r.error ? r.error.message : '';
    ROL_PERMS = (r.data || []).filter((f: any) => f.rol && f.permiso && f.alcance);
    ROLPERM_LEIDO = true;
    //  El botón de reintentar depende de las tres; ésta puede llegar tarde.
    pintarPuerta();
    //  Si el editor está abierto, los avisos pasan de «…» a lo que
    //  diga el rol. Si no está, `refrescarEditor` no hace nada.
    refrescarEditor();
  });

  const [rRoles, rCat] = await Promise.all([
    pedir(sb.from('roles').select('codigo,nombre').eq('activo', true).order('nombre')),
    pedir(sb.from('permisos').select('recurso,accion').order('recurso').order('accion')),
  ]);
  if (viaje !== VIAJE) return;
  E_ROLES    = rRoles.error ? rRoles.error.message : '';
  E_PERMISOS = rCat.error   ? rCat.error.message   : '';
  ROLES        = rRoles.data || [];
  PERMISOS_CAT = rCat.data   || [];
  CATALOGOS_LEIDOS = true;

  //  Un `<div>` de casilleros vacío también afirma "no hay ningún rol": el
  //  vacío se escribe, distinto según el estado.
  //  Ojo: `.innerHTML =` y NO `repintar()`, que es de `<select>` y sobre un
  //  `<div>` no preserva nada. Lo tildado se preserva a mano: la persona ya
  //  llenó el alta cuando reintenta.
  const tildados = new Set([...$$('#uRoles input:checked')].map(i => i.value));
  $('#uRoles').innerHTML = E_ROLES
    ? '<span class="hint">no se pudieron leer los roles</span>'
    : (ROLES.map(r => casillaRol(r, tildados.has(r.codigo)))
        .join('') || '<span class="hint">no hay ningún rol activo</span>');

  //  Ojo: el cartel va en su propio div y NO en `#usrMsg` (el de las acciones),
  //  que `cambiarEstado` y el alta limpian.
  if (E_ROLES)
    aviso('#uRolesMsg', 'bad',
      `No se pudieron leer los roles (${E_ROLES}). No quiere decir que no haya ` +
      `ninguno, y dar de alta ahora crearía a la persona sin ningún rol.`);
  else limpiar('#uRolesMsg');

  //  El catálogo y el mapa de roles se ven en el EDITOR: si está abierto hay
  //  que repintarlo, o queda con el «todavía se están leyendo» y el botón de
  //  Guardar se enciende solo.
  pintarPuerta();
  refrescarEditor();
}

//  Reintentar los catálogos (regla del 13/9): volver a entrar a la pestaña NO
//  los relee (`arranque.ts` sólo llama a `cargarUsuarios`).
$('#uReintentar').onclick = () => cargarRoles();

// ---------- quién puede hacer qué ----------
//  Lo que NO depende de una lectura va ANTES del `await`: si la base tarda
//  (D92) no se ofrece un botón que va a rebotar con 42501.
function pintarPuerta(){
  const alta = $('#uAlta');
  //  Dos motivos para trabar el alta (roles ilegibles o aún sin llegar): los
  //  dos terminan en `p_roles: []`; el cartel dice cuál es.
  const trabaCatalogo = !!E_ROLES || !CATALOGOS_LEIDOS;
  alta.disabled = !gestiona() || trabaCatalogo;
  //  `E_ROLPERM` también enciende el reintento aunque NO trabe nada.
  $('#uReintentar').classList.toggle('oculto', !E_ROLES && !E_PERMISOS && !E_ROLPERM);
  if (!gestiona())
    aviso('#uAltaMsg', 'warn',
      `Podés ver esta pestaña, pero no dar de alta: la base exige «gestionar ` +
      `usuarios» con alcance «todos» y el tuyo es «${esc(alcance('usuario','gestionar') ?? '—')}».`);
  else if (E_ROLES)
    aviso('#uAltaMsg', 'warn',
      'No se puede dar de alta hasta que se lean los roles: sin ellos la ' +
      'persona quedaría creada sin ninguno.');
  else if (!CATALOGOS_LEIDOS)
    aviso('#uAltaMsg', 'warn', 'Todavía se están leyendo los roles.');
  else limpiar('#uAltaMsg');
}

// ---------- el padrón ----------
export async function cargarUsuarios(){
  pintarPuerta();
  //  El permiso se pregunta ANTES de consultar: el RLS devuelve lista vacía,
  //  no error (regla del 4/9). Acá sí se saltea la lectura (D86) porque esta
  //  puerta no recorta: devuelve CERO filas.
  if (!veElPadron()){
    USUARIOS = [];
    $('#tblUsuarios').innerHTML =
      `<tr><td colspan="6" class="vacio">${porQueNoVeElPadron()}</td></tr>`;
    limpiar('#usrLeerMsg');
    //  Este camino no pasa por `pintarUsuarios()`, que escribe ese cartel: sin
    //  este `limpiar` quedaría pegado de una lectura anterior.
    limpiar('#usrSoloMirarMsg');
    cargarEventos();
    return;
  }
  const { data, error } = await pedir(sb.from('v_usuarios').select('*').order('nombre'));
  if (error){
    aviso('#usrLeerMsg', 'bad', 'No pude leer el padrón: ' + error.message);
    //  La tabla también afirma: también se desdice.
    USUARIOS = [];
    $('#tblUsuarios').innerHTML =
      `<tr><td colspan="6" class="vacio">No se pudo leer el padrón ` +
      `(${esc(error.message)}). No quiere decir que no haya nadie cargado.</td></tr>`;
    limpiar('#usrSoloMirarMsg');
    cargarEventos();
    return;
  }
  limpiar('#usrLeerMsg');
  USUARIOS = data || [];
  pintarUsuarios();
  cargarEventos();
}

function estadoDe(u: any){
  if (!u.activo)    return '<span class="chip c-bad">inactivo</span>';
  if (!u.vinculado) return '<span class="chip c-warn">falta 1er ingreso</span>';
  return '<span class="chip c-ok">activo</span>';
}

//  0071 (D164) · A UN ADMIN GLOBAL LO TOCA SÓLO OTRO ADMIN GLOBAL: roles,
//  permisos, estado y datos (`app.tiene_admin_global` / `app.es_admin_global`,
//  que miran el ROL). Los botones se apagan con el motivo escrito; la base lo
//  rechaza igual.
const soyAdminGlobal = () => tengoRol('admin_global');
const esIntocable = (u: any) => (u.roles || []).includes('admin_global') && !soyAdminGlobal();

//  EL ROL QUE NO SE PUEDE DAR, apagado con el motivo. «Admin global» lo da
//  sólo quien tiene la Actividad entera (`auditoria.ver` con alcance todos;
//  nadie otorga lo que no tiene, D116). Los demás roles los decide la base,
//  que dice qué permisos faltan.
const motivoNoDar = (codigo: string) =>
  codigo === 'admin_global' && alcance('auditoria', 'ver') !== 'todos'
    ? 'Lo da sólo otro admin global.' : '';
//  EL PERMISO QUE NO SE DA SUELTO (0072, D169): la Actividad va sólo con el
//  rol «Admin global».
const SOLO_CON_ROL = 'auditoria.ver';
const MOTIVO_SOLO_CON_ROL = 'Va sólo con el rol «Admin global»: no se da suelto.';
const casillaRol = (r: { codigo: string; nombre: string }, tildado: boolean) => {
  const no = motivoNoDar(r.codigo);
  return `<label${no ? ` title="${esc(no)}"` : ''}><input type="checkbox" value="${esc(r.codigo)}"${
    tildado ? ' checked' : ''}${no ? ' disabled' : ''}> ${esc(r.nombre)}${
    no ? ` <span class="hint" data-no-da="${esc(r.codigo)}">(${esc(no)})</span>` : ''}</label>`;
};

//  Botones sólo para quien puede APRETARLOS: con alcance `propio` la base los
//  rebota con 42501. En su lugar va "—" y el motivo se dice UNA vez arriba, en
//  `#usrSoloMirarMsg`.
function accionesDe(u: any){
  if (!gestiona()) return '<span class="hint">—</span>';
  if (esIntocable(u)) return `<div class="fila-acc">
        <button class="b b-sec" data-editar="${u.id}" disabled>Permisos</button>
        <button class="b b-sec" data-estado="${u.id}" disabled>${u.activo ? 'Desactivar' : 'Activar'}</button>
      </div>
      <span class="hint" data-intocable="${u.id}">Es admin global: lo cambia sólo otro admin global.</span>`;
  return `<div class="fila-acc">
        <button class="b b-sec" data-editar="${u.id}">Permisos</button>
        <button class="b b-sec" data-estado="${u.id}">${u.activo ? 'Desactivar' : 'Activar'}</button>
      </div>`;
}

//  EL CHIP DE CADA PERMISO SUELTO, con su alcance (propio vs todo es la
//  diferencia entera). Va en una función: un comentario adentro del template
//  literal de la fila sería texto, no comentario.
function chipsSueltos(u: any){
  const m = sueltosDe(u);
  return Object.keys(m).sort()
    .map(k => `<span class="chip c-warn">${esc(k)} · ${esc(m[k])}</span>`)
    .join(' ') || '—';
}

function pintarUsuarios(){
  $('#tblUsuarios').innerHTML = USUARIOS.map(u => `
    <tr>
      <td><b>${esc(u.nombre)}</b>${u.apodo ? ` <span class="hint">(${esc(u.apodo)})</span>` : ''}</td>
      <td class="mono">${esc(u.email)}</td>
      <td>${(u.roles||[]).map((r: string) => `<span class="chip c-pen">${esc(r)}</span>`).join(' ') || '—'}</td>
      <td>${chipsSueltos(u)}</td>
      <td>${estadoDe(u)}</td>
      <td>${accionesDe(u)}</td>
    </tr>`).join('') || '<tr><td colspan="6" class="vacio">Todavía no hay nadie cargado</td></tr>';
  //  El cartel de "podés mirar y no tocar" va una vez arriba (lee el padrón
  //  por `ver_datos_personales` y las cuatro funciones lo rebotan).
  if (!gestiona())
    aviso('#usrSoloMirarMsg', 'warn',
      `Esto es de sólo lectura: para cambiar roles, permisos o el estado de ` +
      `alguien la base exige «gestionar usuarios» con alcance «todos», y el ` +
      `tuyo es «${esc(alcance('usuario','gestionar') ?? '—')}».`);
  else limpiar('#usrSoloMirarMsg');
}

$('#tblUsuarios').onclick = e => {
  const ed = el(e.target).closest('[data-editar]');
  const es = el(e.target).closest('[data-estado]');
  if (ed) abrirEditor(Number(ed.dataset.editar));
  if (es) cambiarEstado(Number(es.dataset.estado));
};

async function cambiarEstado(id: number){
  //  `Number(x.id) === id` y no `x.id === id`: `v_usuarios.id` es bigint y
  //  PostgREST puede mandarlo como cadena; el `find` fallaría en silencio.
  const u = USUARIOS.find(x => Number(x.id) === id); if (!u) return;
  //  El cartel va en `#usrAccMsg`, junto a la tabla (arriba de todo quedaría
  //  fuera de la vista). `#usrMsg` es el del ALTA.
  limpiar('#usrAccMsg');
  //  Freno del doble click: escribiría dos filas en el rastro y la segunda
  //  llamada corre contra un `USUARIOS` sin releer.
  const btn = $(`[data-estado="${id}"]`);
  if (btn) btn.disabled = true;
  const { data, error } = await rpc('cambiar_estado_usuario',
    { p_usuario_id: id, p_activo: !u.activo });
  // Los rechazos (último admin, uno mismo, el alcance) llegan como error de
  // la base: la regla vive ahí, no en esta pantalla.
  if (error){
    aviso('#usrAccMsg','bad', error.message);
    //  QUINTO estado (D91): si la base no contestó, el cambio PUEDE haber
    //  quedado hecho: el botón no vuelve y se relee. Un rechazo de verdad sí
    //  lo devuelve.
    if (noContesto(error)) cargarUsuarios();
    else if (btn) btn.disabled = false;
    return;
  }
  aviso('#usrAccMsg','ok', data.mensaje);
  cargarUsuarios();
}

// ---------- el alcance que ya da el rol ----------
//  EL MÁXIMO, como hace la base: `app.alcance()` junta en un `union all` lo
//  de los roles y lo de los permisos sueltos y se queda con el más grande
//  (`when bool_or(alcance='todos') then 'todos' …`). Lo fija la prueba de base
//  36 (punto 10).
//
//  Va sobre los roles TILDADOS EN ESTE MOMENTO, no sobre `u.roles`: el aviso
//  dice lo que va a quedar.
//
//  OJO con `roles` INACTIVOS: `#edRoles` filtra `.eq('activo', true)` pero
//  `app.alcance()` NO mira `activo`: un rol inactivo contaría para la base y
//  no para esta cuenta (hoy no es alcanzable).
function alcanceDelRol(permiso: string, rolesTildados: string[]): string | null {
  let mejor: string | null = null;
  for (const f of ROL_PERMS){
    if (f.permiso !== permiso || !rolesTildados.includes(f.rol)) continue;
    if (!mejor || (PESO[f.alcance] || 0) > (PESO[mejor] || 0)) mejor = f.alcance;
  }
  return mejor;
}

//  ¿SE SABE QUÉ LE DA EL ROL? Hacen falta DOS lecturas: `v_rol_permisos` (qué
//  da cada rol) y `roles` (cuáles están tildados; con el catálogo caído no hay
//  casilleros y `rolesTildados` llega VACÍO). Sin la segunda, la grilla
//  afirmaría «rol: —» («el rol no te lo da») sobre todos los permisos.
//
//  Las mitades `!E_ROLES && CATALOGOS_LEIDOS` NO tienen sabotaje a propósito
//  (regla 49): el resultado observable es el mismo, pero dicen el razonamiento
//  y `textoRol` depende de la misma condición.
const seSabeQueDaElRol = () => !E_ROLPERM && ROLPERM_LEIDO && !E_ROLES && CATALOGOS_LEIDOS;

//  Los tres estados de esa columna. Sin cuarta rama para "vino vacía": que un
//  rol no dé un permiso es un vacío LEGÍTIMO y se dice "—".
function textoRol(permiso: string, rolesTildados: string[]){
  if (E_ROLPERM || E_ROLES)            return '<span class="rol">rol: ?</span>';
  if (!ROLPERM_LEIDO || !CATALOGOS_LEIDOS) return '<span class="rol">rol: …</span>';
  const a = alcanceDelRol(permiso, rolesTildados);
  return `<span class="rol">rol: ${a ? esc(a) : '—'}</span>`;
}

// ---------- el editor ----------
//  QUIÉN ESTÁ ABIERTO. El editor se pinta con el estado de los catálogos EN
//  ESE MOMENTO y ese estado cambia después; sin saber quién está abierto no
//  se puede repintar. Dos peligros:
//  · Abrir el editor mientras los catálogos viajan dejaba «todavía se están
//    leyendo» sin repintar; al llegar, `repintarAvisos()` miraba el MÓDULO
//    (`CATALOGOS_LEIDOS` true, `sinElegir` 0) y ENCENDÍA Guardar sobre una
//    grilla vacía: la persona quedaba sin roles ni permisos sueltos.
//  · Si la tercera lectura llegaba ANTES, el freno quedaba puesto y el editor
//    MUERTO.
//  Por eso el editor se REPINTA cuando cambia su estado, preservando lo
//  elegido, y `repintarAvisos` tiene un segundo freno que mira el DOM (que es
//  lo que guardar lee), no el módulo.
let EDITANDO: any = null;
//  Si hay un guardado en vuelo. Lo miran `repintarAvisos` (si no, Guardar se
//  reenciende al tocar un casillero y se manda dos veces) y los botones.
let GUARDANDO = false;

function abrirEditor(id: number){
  //  Con un guardado en vuelo no se abre otra ficha: `guardarPermisos` lee la
  //  grilla y le otorgaría a un usuario los permisos de OTRO.
  if (GUARDANDO) return;
  const u = USUARIOS.find(x => Number(x.id) === id); if (!u) return;
  EDITANDO = u;
  $('#editorUsr').classList.remove('oculto');
  pintarEditor(true);
}

function cerrarEditor(){
  EDITANDO = null;
  const caja = $('#editorUsr');
  caja.classList.add('oculto');
  caja.innerHTML = '';
}

//  PINTA (o REPINTA) LA CAJA. `deCero` es true al abrirla: lo tildado sale de
//  la fila del padrón; en un repintado sale del DOM, para no perder lo elegido.
function pintarEditor(deCero = false){
  const u = EDITANDO; if (!u) return;
  const caja = $('#editorUsr');

  //  Qué había tildado, preguntando por los CONTROLES: con un catálogo caído
  //  "no hay inputs" no quiere decir "no tenía nada tildado" sino que esa
  //  mitad no se pintó y hay que volver a la fila del padrón.
  //  SIN SABOTAJE a propósito (regla 49): hoy `refrescarEditor` sólo repinta con
  //  la grilla NO lista, así que estas banderas son siempre false. Quedan por
  //  si un camino repinta una grilla llena y se pierde lo elegido.
  const habiaRoles = !deCero && $$('#edRoles input[type="checkbox"]').length > 0;
  const habiaPerms = !deCero && $$('#edPerms .pf').length > 0;
  const rolesPuestos = habiaRoles
    ? [...$$('#edRoles input:checked')].map(i => i.value)
    : [...(u.roles || [])];
  const permsPuestos: Record<string, string> = {};
  if (habiaPerms){
    for (const f of $$('#edPerms .pf')){
      const k = f.dataset.perm as string;
      const chk = f.querySelector('input[type="checkbox"]') as HTMLInputElement;
      const sel = f.querySelector('select') as HTMLSelectElement;
      //  El vacío se preserva: tildado sin alcance sigue sin alcance.
      if (chk.checked) permsPuestos[k] = sel.value;
    }
  } else {
    Object.assign(permsPuestos, sueltosDe(u));
  }

  //  Los TRES textos de cada mitad, uno por estado (antes de la primera
  //  respuesta no se puede decir "No hay ningún rol activo").
  const sinLeer = !CATALOGOS_LEIDOS;
  const rolesHtml = E_ROLES
    ? `<div class="vacio">No se pudieron leer los roles (${esc(E_ROLES)}). ` +
      `No quiere decir que no tenga ninguno.</div>`
    : sinLeer
    ? '<div class="vacio">Todavía se están leyendo los roles.</div>'
    : (ROLES.map(r => casillaRol(r, rolesPuestos.includes(r.codigo))).join('')
       || '<div class="vacio">No hay ningún rol activo.</div>');

  caja.innerHTML = `
    <h3 style="margin:1.059rem 0 0.235rem;font-size:.95rem">Permisos de ${esc(u.nombre)}</h3>
    <div class="hint">Roles</div>
    <div class="chips-form" id="edRoles">${rolesHtml}</div>
    <div class="hint" style="margin-top:0.941rem">Permisos sueltos, además del rol</div>
    <div id="edAlcMsg"></div>
    <div class="perms-alc" id="edPerms"></div>
    <div id="edCatMsg"></div>
    <div id="edFaltaMsg"></div>
    <div class="fila-acc" style="margin-top:0.941rem">
      <button class="b b-sec" id="edCancelar">Cancelar</button>
      <button class="b b-pri" id="edGuardar" disabled>Guardar</button>
    </div>
    <div id="edMsg"></div>`;

  pintarPermisos(permsPuestos);

  //  Este cartel también se REPINTA: no puede quedar «en camino» junto a un
  //  botón encendido ni sobrevivir a un catálogo arreglado.
  if (E_ROLES || E_PERMISOS)
    aviso('#edCatMsg', 'warn',
      'No se puede guardar con un catálogo sin leer: lo que no se pudo pintar ' +
      'llegaría como destildado y la base se lo quitaría.');
  else if (sinLeer)
    aviso('#edCatMsg', 'warn',
      'No se puede guardar todavía: los catálogos están en camino y lo que ' +
      'falta pintar llegaría como destildado.');
  else limpiar('#edCatMsg');

  //  Handlers sobre el contenedor: la grilla se repinta y los nodos cambian.
  $('#edPerms').onchange = () => repintarAvisos();
  $('#edRoles').onchange = () => repintarAvisos();
  $('#edCancelar').onclick = () => cerrarEditor();
  $('#edGuardar').onclick = () => guardarPermisos(u);
  repintarAvisos();
}

//  CUANDO CAMBIA EL ESTADO DE LOS CATÁLOGOS con un editor abierto. Repintar
//  los casilleros en cada respuesta movería el foco: sólo se repinta si la caja
//  tiene un estado que ya no es el actual.
function refrescarEditor(){
  if (!EDITANDO) return;
  const grillaLista = $$('#edPerms .pf').length > 0 &&
                      $$('#edRoles input[type="checkbox"]').length > 0;
  const catalogosOk = !E_ROLES && !E_PERMISOS && CATALOGOS_LEIDOS;
  if (grillaLista && catalogosOk) repintarAvisos();
  else pintarEditor();
}

//  LA GRILLA. Cada permiso del catálogo con su casillero, su `<select>` de
//  alcance y lo que le da el rol.
//
//  EL SELECTOR DE UN PERMISO QUE NO SE TENÍA NACE VACÍO (0049): nacer en
//  `todos` ampliaría sin avisar un permiso que era `propio`; vacío obliga a
//  mirarlo una vez. Guardar no sale hasta que todos los tildados tengan uno
//  (`repintarAvisos`). El que YA lo tenía nace con el alcance guardado
//  (`permisos_sueltos`).
function pintarPermisos(mios: Record<string, string>){
  const cont = $('#edPerms');
  if (E_PERMISOS){
    cont.innerHTML = `<div class="vacio">No se pudo leer el catálogo de permisos ` +
      `(${esc(E_PERMISOS)}). No quiere decir que no tenga ninguno suelto.</div>`;
    return;
  }
  if (!CATALOGOS_LEIDOS){
    cont.innerHTML = '<div class="vacio">Todavía se está leyendo el catálogo de permisos.</div>';
    return;
  }
  cont.innerHTML = PERMISOS_CAT.map(p => {
    const k = p.recurso + '.' + p.accion;
    const tiene = Object.prototype.hasOwnProperty.call(mios, k);
    //  0072 (D169) · la Actividad no se da suelta: la base lo rechaza a
    //  cualquiera. Se apaga con el motivo; si alguien ya la tuviera (antes de
    //  la 0071), se puede destildar: quitar sí se puede.
    if (k === SOLO_CON_ROL && !tiene)
      return `<div class="pf" data-perm="${esc(k)}">
        <label title="${esc(MOTIVO_SOLO_CON_ROL)}"><input type="checkbox" value="${esc(k)}" disabled> ${esc(k)}</label>
        <select data-alc="${esc(k)}" disabled><option value="">— elegí —</option></select>
        <span data-rol="${esc(k)}"></span>
        <span class="chip c-warn" data-solo-rol>${esc(MOTIVO_SOLO_CON_ROL)}</span>
      </div>`;
    const opts = ['<option value="">— elegí —</option>']
      .concat(ALCANCES.map(a =>
        `<option value="${a}"${tiene && mios[k] === a ? ' selected' : ''}>${a}</option>`))
      .join('');
    return `<div class="pf" data-perm="${esc(k)}">
        <label><input type="checkbox" value="${esc(k)}"${tiene ? ' checked' : ''}> ${esc(k)}</label>
        <select data-alc="${esc(k)}"${tiene ? '' : ' disabled'}>${opts}</select>
        <span data-rol="${esc(k)}"></span>
      </div>`;
  }).join('') || '<div class="vacio">No hay ningún permiso en el catálogo.</div>';
}

//  QUÉ SE RECALCULA EN CADA CAMBIO: `<select>` habilitado, aviso de rol,
//  tildados sin alcance y cartel de arriba. Todo junto: tocar un ROL cambia
//  todos los avisos y tocar un permiso cambia el estado de Guardar.
function repintarAvisos(){
  //  Puede llegar con el editor cerrado (la tercera lectura contesta tarde).
  if (!document.querySelector('#edPerms')) return;
  const roles = [...$$('#edRoles input:checked')].map(i => i.value);
  const sabeElRol = seSabeQueDaElRol();
  let sinElegir = 0, noRecortan = 0;
  for (const fila of $$('#edPerms .pf')){
    const k   = fila.dataset.perm as string;
    const chk = fila.querySelector('input[type="checkbox"]') as HTMLInputElement;
    const sel = fila.querySelector('select') as HTMLSelectElement;
    const av  = fila.querySelector('[data-rol]') as HTMLElement;
    sel.disabled = !chk.checked;
    //  Destildar limpia el alcance: si no, volver a tildar lo traería sin
    //  que nadie lo mire.
    if (!chk.checked) sel.value = '';
    if (chk.checked && !sel.value) sinElegir++;
    const delRol = sabeElRol ? alcanceDelRol(k, roles) : null;
    //  Aviso de que el suelto NO HACE NADA: se sabe qué da el rol, hay alcance
    //  elegido y el del rol es igual o mayor (la base toma el máximo).
    const nada = !!(delRol && chk.checked && sel.value &&
                    (PESO[delRol] || 0) >= (PESO[sel.value] || 0));
    if (nada) noRecortan++;
    av.innerHTML = textoRol(k, roles) +
      (nada ? ' <span class="chip c-warn">no cambia nada</span>' : '');
  }
  //  El cartel de la tercera lectura, UNA vez arriba de la grilla; el de "no
  //  cambia nada" también se resume acá (el chip dice QUÉ, no POR QUÉ).
  //  `E_ROLPERM` va sin `esc()`: `aviso()` ya escapa (`avisoHtml` no).
  if (E_ROLPERM)
    aviso('#edAlcMsg', 'warn',
      `No se pudo leer con qué alcance da cada rol sus permisos (${E_ROLPERM}), ` +
      `así que abajo dice «rol: ?». Se puede guardar igual —lo que se guarda es ` +
      `el alcance que elijas—, pero ojo: la base se queda con el MÁS GRANDE entre ` +
      `el del rol y éste, así que uno más chico puede no cambiar nada.`);
  else if (E_ROLES)
    aviso('#edAlcMsg', 'warn',
      'Abajo dice «rol: ?» porque no se pudieron leer los roles: sin ellos no se ' +
      'sabe cuáles tiene esta persona, y por lo tanto tampoco qué le dan.');
  else if (!ROLPERM_LEIDO || !CATALOGOS_LEIDOS)
    aviso('#edAlcMsg', 'warn',
      'Todavía se está leyendo con qué alcance da cada rol sus permisos.');
  else if (noRecortan)
    aviso('#edAlcMsg', 'warn',
      `Hay ${noRecortan} permiso${noRecortan > 1 ? 's' : ''} donde el rol ya da lo ` +
      `mismo o más: la base se queda con el MÁS GRANDE de los dos, así que ` +
      `otorgarlo con un alcance igual o menor no cambia nada. Para recortar de ` +
      `verdad hay que sacarle el rol.`);
  else limpiar('#edAlcMsg');

  //  GUARDAR. Cuatro frenos:
  //   · catálogo caído o en viaje (`trabaCat`): el estado del MÓDULO;
  //   · guardado en vuelo (si no, se manda dos veces al tocar un casillero);
  //   · **grilla pintada de verdad** (`grillaLista`): el estado del DOM. Lo que
  //     `guardarPermisos` lee es el DOM, así que el freno mira el DOM; con la
  //     caja en «todavía se está leyendo» guardaría la lista vacía;
  //   · ningún permiso tildado sin alcance.
  //  `E_ROLPERM` NO traba: es información.
  //
  //  Ojo: `trabaCat` y `grillaLista` SE TAPAN ENTRE SÍ con un catálogo caído;
  //  sacar uno solo no pone roja ninguna prueba (el sabotaje quita los dos).
  //  No sobra ninguno: `grillaLista` atrapa el desacuerdo entre DOM y módulo.
  const trabaCat = !!E_ROLES || !!E_PERMISOS || !CATALOGOS_LEIDOS;
  const grillaLista = $$('#edPerms .pf').length > 0 &&
                      $$('#edRoles input[type="checkbox"]').length > 0;
  const btn = $('#edGuardar');
  if (btn) btn.disabled = GUARDANDO || trabaCat || !grillaLista || sinElegir > 0;
  //  En su PROPIO div y no en `#edMsg` (resultado de guardar): si no, tildar
  //  un casillero borraría el rojo de "cambios a medias". El texto NO dice
  //  «recién tildado»: `— elegí —` se ofrece también en permisos ya tenidos.
  if (sinElegir)
    aviso('#edFaltaMsg', 'warn',
      `Falta elegir el alcance de ${sinElegir} permiso${sinElegir > 1 ? 's' : ''} ` +
      `tildado${sinElegir > 1 ? 's' : ''}. Antes se otorgaban siempre sobre ` +
      `«todos» sin preguntar, y por eso ahora hay que decirlo.`);
  else limpiar('#edFaltaMsg');
}

//  GUARDAR SON VARIAS LLAMADAS, NO UNA TRANSACCIÓN: `asignar_roles` primero y
//  luego una por permiso; si la tercera falla, las dos primeras YA QUEDARON
//  HECHAS. Por eso el error también relee el padrón (`finally`) y el cartel
//  avisa del "a medias".
//
//  `asignar_roles` NO se llama si los roles no cambiaron: no es optimización,
//  la función hace `delete` + `insert` y escribe SIEMPRE una fila
//  `roles_cambiados` en el rastro (un cambio que no ocurrió).
async function guardarPermisos(u: any){
  const btn = $('#edGuardar'); btn.disabled = true;
  GUARDANDO = true;
  //  Cancelar también se apaga: vaciar la caja a mitad dejaría el `for` sin
  //  filas y le quitaría todos sus permisos sueltos, sin cartel.
  const cancelar = $('#edCancelar'); if (cancelar) cancelar.disabled = true;
  limpiar('#edMsg'); limpiar('#edFaltaMsg'); limpiar('#usrAccMsg');
  let hechas = 0;
  try {
    //  LA FOTO DEL DOM SE TOMA ENTERA, ACÁ, ANTES DEL PRIMER `await`: después
    //  la grilla puede ser otra (cancelada o la ficha de OTRA persona). Regla
    //  63 al revés: un handler con `await` no puede LEER después el DOM que
    //  miró antes.
    const roles = [...$$('#edRoles input:checked')].map(i => i.value);
    const ahora: Record<string, string> = {};
    for (const fila of $$('#edPerms .pf')){
      const k   = fila.dataset.perm as string;
      const chk = fila.querySelector('input[type="checkbox"]') as HTMLInputElement;
      const sel = fila.querySelector('select') as HTMLSelectElement;
      if (chk.checked && sel.value) ahora[k] = sel.value;
    }

    const antesRoles = [...(u.roles || [])].sort();
    if ([...roles].sort().join(',') !== antesRoles.join(',')){
      const { error: e1 } = await rpc('asignar_roles',
        { p_usuario_id: u.id, p_roles: roles });
      if (e1) throw e1;
      hechas++;
    }

    //  TRES LISTAS Y NO DOS: además de los que se tildan/destildan está el
    //  permiso al que se CAMBIÓ el alcance; comparar sólo nombres no lo
    //  detectaría.
    const antes: Record<string, string> = sueltosDe(u);
    //  Otorgar cubre el que no estaba y el que cambió de alcance: en la base
    //  es la misma llamada (`on conflict (usuario_id, permiso_id) do update
    //  set alcance = excluded.alcance`).
    const otorgar = Object.keys(ahora).filter(k => antes[k] !== ahora[k]).sort();
    //  Quitar va con el alcance QUE TENÍA, no con `'todos'`: la función lo
    //  escribe en el rastro y otro valor anotaría un hecho falso (D104).
    const quitar = Object.keys(antes).filter(k => !(k in ahora)).sort();

    //  QUITAR VA PRIMERO: sin transacción, un corte a mitad dejaría a la
    //  persona con más permisos de los queridos; así queda del lado seguro.
    for (const k of quitar.concat(otorgar)){
      const i = k.indexOf('.');
      const recurso = k.slice(0, i), accion = k.slice(i + 1);
      const otorga = k in ahora;
      const { error } = await rpc('cambiar_permiso_usuario', {
        p_usuario_id: u.id, p_recurso: recurso, p_accion: accion,
        p_otorgar: otorga, p_alcance: otorga ? ahora[k] : antes[k]
      });
      if (error) throw error;
      hechas++;
    }

    cerrarEditor();
    aviso('#usrAccMsg','ok', hechas ? 'Permisos actualizados.' : 'No había nada que cambiar.');
  } catch (err: any) {
    //  El cartel puede no tener dónde ir: si la caja se cerró, `#edMsg` no
    //  existe y `aviso()` tiraría TypeError; se cae a `#usrAccMsg`, que siempre
    //  está. Sin sabotaje: hoy la caja no puede cerrarse en vuelo.
    const donde = document.querySelector('#edMsg') ? '#edMsg' : '#usrAccMsg';
    aviso(donde,'bad', (err.message || String(err)) + (hechas
      ? ' Ojo: los ' + hechas + ' cambios anteriores ya quedaron hechos. ' +
        'Cerrá el editor y volvé a abrirlo antes de reintentar.'
      : ''));
    //  Si quedó a medias, el editor no sirve más: su `u` es el de antes y
    //  reintentar mandaría de nuevo lo ya hecho (un `roles_cambiados` con
    //  antes == ahora). Guardar queda apagado hasta reabrir.
    if (hechas) EDITANDO = null;
  } finally {
    GUARDANDO = false;
    if (cancelar && document.querySelector('#edCancelar')) cancelar.disabled = false;
    //  Se relee SIEMPRE: si el error llegó a mitad
    //  de camino, la tabla de arriba ya no dice la verdad.
    cargarUsuarios();
    //  El botón vuelve sólo si el editor sigue sirviendo (`EDITANDO` puede
    //  haber quedado en null).
    if (EDITANDO) repintarAvisos();
    else if (document.querySelector('#edGuardar')) $('#edGuardar').disabled = true;
  }
}

const TEXTO_EVENTO: Record<string, string> = {
  creado: 'Alta', editado: 'Datos editados', activado: 'Activado',
  desactivado: 'Desactivado', roles_cambiados: 'Roles',
  permiso_otorgado: 'Permiso otorgado', permiso_quitado: 'Permiso quitado'
};

//  EL DETALLE DEL RASTRO. `d.alcance` lo guarda la base desde la 0010 y es lo
//  que hay que poder auditar. Se muestra sólo si está: un `?? 'todos'` sería
//  inventar un dato que no se leyó (D83).
function detalleEvento(e: any){
  const d = e.detalle || {};
  if (e.evento === 'roles_cambiados')
    return ` <span class="hint">${esc((d.antes||[]).join(', ')) || 'sin rol'} → ${
      esc((d.ahora||[]).join(', ')) || 'sin rol'}</span>`;
  if (d.permiso) return ` <span class="mono hint">${esc(d.permiso)}${
    d.alcance ? ' · ' + esc(d.alcance) : ''}</span>`;
  if (e.evento === 'creado' && (d.roles||[]).length)
    return ` <span class="hint">${esc((d.roles||[]).join(', '))}</span>`;
  return '';
}

//  EL RASTRO. Ojo: no tragarse el `error`; una lectura caída se vería como
//  "Sin movimientos todavía".
//
//  Tiene su propio recorte: con alcance `propio` llegan las filas PROPIAS; la
//  lista está RECORTADA (D97) y hay que decirlo.
async function cargarEventos(){
  const { data, error } = await pedir(sb.from('v_usuario_eventos')
    .select('*').order('hecho_en', { ascending: false }).limit(30));
  if (error){
    aviso('#usrEvMsg', 'bad', 'No pude leer el rastro del padrón: ' + error.message);
    $('#tblUsrEventos').innerHTML =
      `<tr><td colspan="4" class="vacio">No se pudo leer qué se cambió ` +
      `(${esc(error.message)}). No quiere decir que no se haya cambiado nada.</td></tr>`;
    return;
  }
  const filas = data || [];
  if (!gestiona())
    aviso('#usrEvMsg', 'warn',
      `Esto es sólo lo tuyo: con «gestionar usuarios» en alcance ` +
      `«${esc(alcance('usuario','gestionar') ?? '—')}» la base manda nada más ` +
      `que los cambios sobre tu propio usuario, no los de todos.`);
  else limpiar('#usrEvMsg');
  $('#tblUsrEventos').innerHTML = filas.map((e: any) => `
    <tr><td class="hint">${new Date(e.hecho_en).toLocaleString('es-AR')}</td>
        <td>${esc(e.usuario)}</td>
        <td>${TEXTO_EVENTO[e.evento] || esc(e.evento)}${detalleEvento(e)}</td>
        <td>${esc(e.hecho_por || '—')}</td></tr>`).join('')
    || `<tr><td colspan="4" class="vacio">${gestiona()
          ? 'Sin movimientos todavía'
          : 'No hay ningún cambio sobre tu propio usuario'}</td></tr>`;
}

$('#formUsr').onsubmit = async e => {
  e.preventDefault();
  limpiar('#usrMsg');
  const btn = $('#uAlta'); btn.disabled = true;
  const roles = [...$$('#uRoles input:checked')].map(i => i.value);
  const { data, error } = await rpc('crear_usuario', {
    p_email   : $('#uEmail').value.trim(),
    p_nombre  : $('#uNombre').value.trim(),
    p_apodo   : $('#uApodo').value.trim() || null,
    p_telefono: null,
    p_roles   : roles
  });
  if (error){
    aviso('#usrMsg','bad', error.message);
    //  Si la base RECHAZÓ, no creó a nadie: el botón vuelve. Si NO CONTESTÓ
    //  (D91), puede haber quedado creada: el botón queda apagado y se relee.
    if (noContesto(error)) cargarUsuarios();
    else pintarPuerta();
    return;
  }
  aviso('#usrMsg', data.vinculado ? 'ok' : 'warn', data.mensaje);
  $('#formUsr').reset();
  pintarPuerta();
  cargarUsuarios();
};
